import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  DoCheck,
  IterableDiffers,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  inject,
  input,
  model,
  output,
} from '@angular/core';
import {
  NgbNav,
  NgbNavItem,
  NgbNavItemRole,
  NgbNavLink,
  NgbNavLinkBase,
  NgbNavContent,
  NgbNavOutlet,
} from '@ng-bootstrap/ng-bootstrap';

import { TabTemplate } from '../../models';
import { ModalService } from '../../services';
import { FormService } from '../../services/component-services/form.service';

@Component({
  selector: 'app-tab',
  templateUrl: './tab.component.html',
  styleUrl: './tab.component.scss',
  imports: [
    NgbNav,
    NgbNavItem,
    NgbNavItemRole,
    NgbNavLink,
    NgbNavLinkBase,
    NgTemplateOutlet,
    NgbNavContent,
    NgbNavOutlet,
  ],
})
export class TabComponent implements OnInit, OnChanges, DoCheck {
  private readonly formService = inject(FormService);
  private readonly modalService = inject(ModalService);
  private readonly differs = inject(IterableDiffers);

  public readonly model = input<any>();
  public readonly templates = input<TabTemplate[]>([]);
  public readonly showAll = input(false);
  public readonly fullWidth = input(false);
  public readonly hasModel = input(true);
  public readonly highlightText = input(false);
  public readonly activeId = model<string | number | undefined>();

  public readonly activeIdChange = output<string | number>();

  protected isModal: boolean;

  private differ = this.differs
    .find([])
    .create<TabTemplate>((index, template) => template.key ?? template.header ?? index);

  isTemplateRef(value: unknown): value is TemplateRef<any> {
    return value instanceof TemplateRef;
  }

  ngOnInit() {
    this.isModal = this.modalService.anyModalsOpen;
    this.ensureActiveSelectedAndLoaded();
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('templates' in changes) this.ensureActiveSelectedAndLoaded();
  }

  ngDoCheck() {
    if (this.differ.diff(this.templates())) {
      this.ensureActiveSelectedAndLoaded();
    }
  }

  onActiveIdChange(id: string | number | undefined) {
    this.activeId.set(id);
    if (id) this.activeIdChange.emit(id!);
    this.ensureLoaded(id);
  }

  ensureLoaded(id: string | number | undefined) {
    if (id == null) return;
    const idStr = String(id);
    const hit = this.items.find((x) => x.id === idStr);
    if (hit && !hit.t.loaded) hit.t.loaded = true;
  }

  get items() {
    const src = this.templatesToShow;
    return src.map((template, index) => ({
      id: String(template.key ?? template.header ?? index),
      t: template,
    }));
  }

  get templatesToShow(): TabTemplate[] {
    const visible = (this.templates() ?? []).filter((t) => !t.hide);
    return this.hasModel() && this.formService.isNewModel(this.model()) && !this.showAll()
      ? visible.slice(0, 1)
      : visible;
  }

  loadTemplate(template: TabTemplate) {
    template.loaded = true;
  }

  private ensureActiveSelectedAndLoaded() {
    const arr = this.items;
    if (!arr?.length) return;

    const ids = new Set(arr.map((a) => a.id));
    if (this.activeId() == null || !ids.has(String(this.activeId()))) {
      this.activeId.set(arr[0].id);
      this.activeIdChange.emit(this.activeId());
    }

    const hit = arr.find((x) => x.id === this.activeId())!;
    if (!hit.t.loaded) hit.t.loaded = true;

    this.ensureLoaded(this.activeId());
  }
}
